import React, { ComponentType } from 'react'
import { connect } from 'react-redux'
import {
  Container,
  Button,
  Grid,
  Icon,
  Message,
  Segment
} from 'semantic-ui-react'
import {
  NotificationContainer,
  NotificationManager
} from 'react-notifications'
import 'react-notifications/lib/notifications.css'
// Local
import { Dispatch } from 'redux'
import {
  API_URL,
  ATHENA_CHROME_PLUGIN_URL,
  ATHENA_EDGE_PLUGIN_URL,
  CRON_JOB,
  shouldRenderReskin2025
} from '../../constant'
import { api } from '../../action/api'
import { initiateResetPassword } from '../../action/user'
import { formatRoleName } from '../common/formatUtils'
import { isAthena } from '../common/tenantUtils'
import RoleSelect from './RoleSelect'
import UserPropertyDetails from './UserPropertyDetails'
import If from '../common/if'
import ReveleerButton from '../common/ReveleerButton'
import { RootState } from '../../store'
// Styling
import './index.css'
import ReveleerCoachingBox from '../ReveleerCoachingBox'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  initiateResetPasswordProp: (callback: (data: unknown) => void) =>
    dispatch(initiateResetPassword(callback))
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>
type ReduxDispatchProps = ReturnType<typeof mapDispatchToProps>

type Props = ReduxStateProps & ReduxDispatchProps

function Account(
  props: Props
) {
  const {
    currentUser,
    initiateResetPasswordProp
  } = props

  const handleResetPasswordClick = () => {
    initiateResetPasswordProp(() => {
      NotificationManager.success('An email has been sent with a link to reset your password.')
    })
  }

  const handleResetDemoDataClick = (
    tenantId: string
  ): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const url = `${API_URL.CRON_JOB_EXECUTE(CRON_JOB.DEMO_DATA_JOB_ID, tenantId)}`
    const cronJobParamsBody: string[] = [] // leave empty for now -- demo data job doesn't need params to run
    api.post(url, cronJobParamsBody).then(() => {
      NotificationManager.success('Successfully initiated demo data reset'
        + '. The data for this demo tenant will be refreshed momentarily.')
    }).catch(error => {
      NotificationManager.error('Something went wrong, please try the demo data reset again.')
      console.log('API error from attempt to call demo data reset:')
      console.log(error)
    })
  }

  const isDemoOrDevEnvironment = (): boolean => (
    window.location.href.includes('localhost')
    || window.location.href.includes('demo')
  )

  const activeRole = currentUser.type
  const roles = currentUser.roles
    ? currentUser.roles
    : []
  const userProperties = currentUser.userProperties
    ? currentUser.userProperties
    : []
  const { tenantId } = currentUser
  return (
    <div className="header-padding">
      <Container>
        <NotificationContainer />
        <SafeSegment>
          <SafeGrid>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <h3>
                  User Role
                </h3>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <span>
                  Active User Role:
                </span>
                <span>&nbsp;</span>
                <span>
                  <strong>
                    { formatRoleName(activeRole) }
                  </strong>
                </span>
              </Grid.Column>
            </Grid.Row>
            <If isTrue={ roles.length > 1 }>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                >
                  <RoleSelect />
                </Grid.Column>
              </Grid.Row>
            </If>
          </SafeGrid>
        </SafeSegment>
        <If isTrue={ isDemoOrDevEnvironment() }>
          <SafeSegment>
            <SafeGrid>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                >
                  <h3>
                    Demo Data Reset
                  </h3>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                >
                  <If isTrue={ !shouldRenderReskin2025 }>
                    <Message>
                      <Icon name="info circle" />
                      Click &quot;Reset Demo Data&quot; to initiate resetting the demo data back
                      to the defaults for this demo tenant.
                    </Message>
                  </If>
                  <If isTrue={ shouldRenderReskin2025 }>
                    <ReveleerCoachingBox
                      coachingBoxType="infoNegative"
                      isShowing
                      message={
                        <p>
                          Click &quot;Reset Demo Data&quot; to initiate resetting the demo data back
                          to the defaults for this demo tenant.
                        </p>
                      }
                    />
                  </If>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                  textAlign="right"
                >
                  <If isTrue={ !shouldRenderReskin2025 }>
                    <Button
                      content="Reset Demo Data"
                      id="reset-demo-data-button"
                      onClick={ () => handleResetDemoDataClick(tenantId) }
                    />
                  </If>
                  <If isTrue={ shouldRenderReskin2025 }>
                    <ReveleerButton
                      buttonLabel="Reset Demo Data"
                      buttonSize="small"
                      buttonType="secondary"
                      id="reset-demo-data-button"
                      isDisabled={ false }
                      onClick={ () => handleResetDemoDataClick(tenantId) }
                      type="button"
                    />
                  </If>
                </Grid.Column>
              </Grid.Row>
            </SafeGrid>
          </SafeSegment>
        </If>
        <If isTrue={ userProperties.length > 0 }>
          <SafeSegment>
            <SafeGrid>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                >
                  <h3>
                    User Properties
                  </h3>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                >
                  <UserPropertyDetails currentUser={ currentUser } />
                </Grid.Column>
              </Grid.Row>
            </SafeGrid>
          </SafeSegment>
        </If>
        <SafeSegment>
          <SafeGrid>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <h3>
                  Password Reset
                </h3>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <If isTrue={ !shouldRenderReskin2025 }>
                  <Message>
                    <Icon name="info circle" />
                    Click &quot;Reset Password&quot; to initiate a password reset. You will
                    receive an email with a link and directions for changing your
                    password.
                  </Message>
                </If>
                <If isTrue={ shouldRenderReskin2025 }>
                  <ReveleerCoachingBox
                    coachingBoxType="infoNegative"
                    isShowing
                    message={
                      <p>
                        Click &quot;Reset Password&quot; to initiate a password reset. You will
                        receive an email with a link and directions for changing your
                        password.
                      </p>
                    }
                  />
                </If>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
                textAlign="right"
              >
                <If isTrue={ !shouldRenderReskin2025 }>
                  <Button
                    content="Reset Password"
                    id="reset-password-button"
                    onClick={ handleResetPasswordClick }
                  />
                </If>
                <If isTrue={ shouldRenderReskin2025 }>
                  <ReveleerButton
                    buttonLabel="Reset Password"
                    buttonSize="small"
                    buttonType="secondary"
                    id="reset-password-button"
                    isDisabled={ false }
                    onClick={ handleResetPasswordClick }
                    type="button"
                  />
                </If>
              </Grid.Column>
            </Grid.Row>
          </SafeGrid>
        </SafeSegment>
        <If isTrue={ isAthena(tenantId) }>
          <SafeSegment>
            <SafeGrid>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                >
                  <h3>
                    Athena Browser Plugin
                  </h3>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column
                  mobile={ 16 }
                  tablet={ 16 }
                  computer={ 16 }
                >
                  <span>
                    Platform integration with Athena is provided with a browser
                    plugin. The plugin can be downloaded and installed from the
                  </span>
                  <span>&nbsp;</span>
                  <span>
                    <a
                      href={ ATHENA_CHROME_PLUGIN_URL }
                      target="_blank"
                      rel="noopener noreferrer">
                      Google Chrome Web Store
                    </a>
                  </span>
                  <span>&nbsp;and the&nbsp;</span>
                  <span>
                    <a
                      href={ ATHENA_EDGE_PLUGIN_URL }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Microsoft Edge Web Store
                    </a>
                  </span>
                </Grid.Column>
              </Grid.Row>
            </SafeGrid>
          </SafeSegment>
        </If>
      </Container>
    </div>
  )
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Account)
